import cn from 'classnames';
import { ReactNode, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { MdDeleteOutline } from 'react-icons/md';
import { DatePicker } from '@Components/DatePicker';
import { Appearance, Design, Placement, Size, StretchMode } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { InfoTooltip } from '@Components/InfoTooltip';
import { IconButton } from '@Components/IconButton';
import { Header } from '@Components/StickyTable';
import { Warning700 } from '@src/utils/colors';
import { getFormattedValue } from '@Components/NumberFormatter/NumberFormatter';
import {
  ALLOW_ALL_DATES,
  formatLegacyDate,
  GoalRow,
  GoalSelectorOptions,
  MULTI_VALUE_SEGMENTS,
  OptionsKey,
  STANDARD_CALENDAR,
  TableItem
} from './goal-intake-constants';
import {
  asNames,
  hasTvadChannel,
  hasPlatform,
  isChannelOptionDisabled,
  isDecimalGoal,
  mixesTvadChannels,
  requiresKpi
} from './goal-intake-utils';

interface RowEditProps {
  row: GoalRow;
  onChange: (row: GoalRow) => void;
}

interface SelectCellProps {
  field: string;
  optionsKey: string;
  isMulti: boolean;
  row: GoalRow;
  selectorOptions: GoalSelectorOptions;
  hasError: boolean;
  onChange: (row: GoalRow) => void;
}

// Menus render fixed at document level so the table's horizontal scroll doesn't clip them.
const MENU_PROPS = { menuPosition: 'fixed' as const, isValidNewOption: () => false };

// Judged against the dropdown's live selection on every render, so the other group opens up the
// moment the last checked option is cleared.
const getChannelMenuProps = (platformSet: boolean) => ({
  ...MENU_PROPS,
  isOptionDisabled: (option: { value: string }, selected: readonly { value: string }[]) =>
    isChannelOptionDisabled(
      selected.map(({ value }) => value),
      option.value,
      platformSet
    )
});

const SelectCell = ({
  field,
  optionsKey,
  isMulti,
  row,
  selectorOptions,
  hasError,
  onChange
}: SelectCellProps) => {
  const current = row[field];
  const options = useMemo(() => {
    const mapped = (selectorOptions[optionsKey] ?? []).map(({ label, value }) => ({
      label: String(label),
      value: String(value)
    }));
    // The API returns these options unsorted.
    return SORTED_OPTIONS_KEYS.has(optionsKey)
      ? mapped.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
      : mapped;
  }, [selectorOptions, optionsKey]);
  const common = {
    size: Size.MD,
    options,
    searchable: true as const,
    clearable: true,
    hasError,
    menuPortalTarget: document.body,
    reactSelectProps: field === 'Channel' ? getChannelMenuProps(hasPlatform(row)) : MENU_PROPS,
    // Platform doesn't apply to TVAD channels; the value is kept, but a row that already has both
    // stays editable so the Platform can be cleared.
    disabled: field === 'Platform' && hasTvadChannel(row) && !hasPlatform(row),
    ariaLabel: field
  };
  return isMulti ? (
    <Dropdown
      {...common}
      multiSelect
      // Chips show every selected value in full instead of "N items selected".
      showChips
      value={asNames(current)}
      onChange={values => onChange({ ...row, [field]: values })}
    />
  ) : (
    <Dropdown
      {...common}
      value={current == null || Array.isArray(current) ? null : String(current)}
      onChange={(value: string | null) => onChange({ ...row, [field]: value })}
    />
  );
};

// Dollar goals take cash-register entry (digits fill in from the cents place); ratio and count
// goals (ROAS, CTR, KPI Volume) take a plain number.
export const GoalValueField = ({ row, onChange }: RowEditProps) => {
  const value = row.goal_value;
  const setValue = (goalValue: number | null) => onChange({ ...row, goal_value: goalValue });

  if (isDecimalGoal(row.goal_name)) {
    return (
      <Form.Control
        className='goal-value-input'
        type='number'
        data-testid='field-goal_value'
        value={value ?? ''}
        onChange={e => {
          const parsed = parseFloat(e.target.value);
          setValue(Number.isNaN(parsed) ? null : parsed);
        }}
      />
    );
  }

  return (
    <Form.Control
      className='goal-value-input'
      data-testid='field-goal_value'
      value={value == null ? '' : getFormattedValue({ value, type: '$', decimals: 2 })}
      onChange={e => {
        const cents = parseInt(e.target.value.replace(/[^0-9]/g, ''), 10);
        setValue(Number.isNaN(cents) ? null : cents / 100);
      }}
    />
  );
};

// Option lists sorted by label; the other fields keep their order (Goal Type is a fixed list).
const SORTED_OPTIONS_KEYS: ReadonlySet<string> = new Set([
  'Channel',
  OptionsKey.KPI,
  OptionsKey.CAMPAIGN,
  OptionsKey.AD_GROUP,
  OptionsKey.SOURCE
]);

// Fits the slim date button and the row's 8px side padding. The wider marker labels spill over
// the neighbouring marker cells instead.
const DATE_COLUMN_WIDTH = 136;

// A legacy row's amount can be edited and the row deleted;
// its dates, Channel and goal type are fixed. These columns render like any other row.
const LEGACY_EDITABLE_COLUMNS: ReadonlySet<string> = new Set([
  'legacy_marker',
  'goal_value',
  'delete'
]);
const MARKER_COLUMN_WIDTH = 40;

export const getLegacyTooltip = (row: GoalRow): ReactNode => (
  <>
    This booking was carried over from the old Expected Bookings page. You can edit the goal value
    or delete the row. The dates, channel and goal type are fixed.
    {row.legacyConflict && (
      <>
        <br />
        This amount takes priority over the other entry for the same week and channel.
      </>
    )}
  </>
);

interface WarningMarkerProps {
  testId: string;
  children: ReactNode;
}

const WarningMarker = ({ testId, children }: WarningMarkerProps) => (
  <span className='goal-intake-legacy-marker'>
    <InfoTooltip
      placement={Placement.RIGHT}
      iconColor={Warning700}
      data-testid={testId}
      aria-label='Details'
    >
      {children}
    </InfoTooltip>
  </span>
);

interface LegacyMarkerProps {
  row: GoalRow;
}

export const LegacyMarker = ({ row }: LegacyMarkerProps) => (
  <WarningMarker testId='goal-intake-legacy-marker'>{getLegacyTooltip(row)}</WarningMarker>
);

export const MixedChannelMarker = () => (
  <WarningMarker testId='goal-intake-mixed-channel-marker'>
    This budget combines TVAD channels (Streaming Video, Video, Streaming Audio, Podcast, Linear,
    Radio) with other channels. TVAD channel budgets cannot be combined with any other channel.
  </WarningMarker>
);

const getSegmentLabel = (segment: string) =>
  MULTI_VALUE_SEGMENTS.has(segment) ? `${segment}(s)` : segment;

const CHANNEL_HEADER_LABEL = getSegmentLabel('Channel');

// Plain label for every column but Channel, which gets an info icon.
export const renderGoalHeader = ({ data }: { data: unknown }) => {
  if (data !== CHANNEL_HEADER_LABEL) return <>{typeof data === 'string' ? data : null}</>;
  return (
    <>
      {data}{' '}
      <InfoTooltip
        placement={Placement.TOP}
        data-testid='goal-intake-channel-info'
        aria-label='Details'
      >
        TVAD channel budgets cannot be combined with any other channel.
      </InfoTooltip>
    </>
  );
};

// What a legacy (expected_bookings) row shows in the columns that are not editable.
const getLegacyContent = (name: string, row: GoalRow): ReactNode => {
  switch (name) {
    case 'start_date':
    case 'end_date': {
      const date = row[name];
      // Same format as the DatePicker buttons on editable rows.
      return typeof date === 'string' ? formatLegacyDate(date) : null;
    }
    default: {
      const value = row[name];
      if (Array.isArray(value)) return value.join(', ');
      return value == null ? null : String(value);
    }
  }
};

type ColumnDef = Omit<Header, 'renderer'> & { renderer: (item: TableItem) => ReactNode };

interface BuildHeadersArgs {
  customSegments: string[];
  selectorOptions: GoalSelectorOptions;
  unsavedKeys: Set<string>;
  errors: Map<string, string>;
  onChange: (key: string, row: GoalRow) => void;
  onDelete: (key: string) => void;
}

// Columns in order: view group, dates, the company's segments, goal type/value, KPI + source,
// campaign, ad group, error, delete. Editable cells are CELL-variant Dropdowns rendered from the
// header's renderer.
export const buildGoalHeaders = ({
  customSegments,
  selectorOptions,
  unsavedKeys,
  errors,
  onChange,
  onDelete
}: BuildHeadersArgs): Header[] => {
  const cell = (draft: TableItem, content: ReactNode) => (
    <div className={cn('goal-intake-cell', { 'unsaved-cell': unsavedKeys.has(draft.key) })}>
      {content}
    </div>
  );
  const select = (draft: TableItem, field: string, optionsKey: string, isMulti: boolean) =>
    cell(
      draft,
      <SelectCell
        field={field}
        optionsKey={optionsKey}
        isMulti={isMulti}
        row={draft.row}
        selectorOptions={selectorOptions}
        hasError={errors.has(draft.key)}
        onChange={row => onChange(draft.key, row)}
      />
    );
  const kpiSelect = (draft: TableItem, field: string, optionsKey: string) =>
    requiresKpi(draft.row) ? select(draft, field, optionsKey, false) : cell(draft, null);

  const columns: ColumnDef[] = [
    {
      label: '',
      name: 'legacy_marker',
      width: MARKER_COLUMN_WIDTH,
      nonInteractive: true,
      renderer: (draft: TableItem) =>
        draft.row.legacy || mixesTvadChannels(draft.row) ? (
          <div className='goal-intake-cell'>
            {draft.row.legacy ? <LegacyMarker row={draft.row} /> : <MixedChannelMarker />}
          </div>
        ) : null
    },
    ...(['start_date', 'end_date'] as const).map(field => ({
      label: field === 'start_date' ? 'Start Date' : 'End Date',
      name: field,
      width: DATE_COLUMN_WIDTH,
      nonInteractive: true,
      renderer: (draft: TableItem) => {
        const date = draft.row[field] ?? '';
        return cell(
          draft,
          <DatePicker
            isSingle
            numVisibleMonths={1}
            hideDateInput
            // An empty override is a standard calendar; it also drops the calendar-settings wrench.
            fiscalCalOverride={STANDARD_CALENDAR}
            size={Size.MD}
            stretch={StretchMode.FILL}
            appearance={Appearance.OUTLINED}
            design={Design.NEUTRAL}
            placeholder='mm/dd/yyyy'
            range={{ start: date, end: date }}
            isOutsideRange={ALLOW_ALL_DATES}
            hasError={errors.has(draft.key)}
            onChange={({ start }) => onChange(draft.key, { ...draft.row, [field]: start })}
          />
        );
      }
    })),
    ...customSegments.map(segment => ({
      label: getSegmentLabel(segment),
      name: segment,
      flex: true,
      // Multi-value segments (Channel) need room for several values.
      minFlexWidth: MULTI_VALUE_SEGMENTS.has(segment) ? 320 : 200,
      nonInteractive: true,
      renderer: (draft: TableItem) =>
        select(draft, segment, segment, MULTI_VALUE_SEGMENTS.has(segment))
    })),
    {
      label: 'Goal Type',
      name: 'goal_name',
      flex: true,
      minFlexWidth: 200,
      nonInteractive: true,
      renderer: (draft: TableItem) => select(draft, 'goal_name', OptionsKey.GOAL_NAME, false)
    },
    {
      label: 'Goal Value',
      name: 'goal_value',
      width: 180,
      nonInteractive: true,
      renderer: (draft: TableItem) =>
        cell(draft, <GoalValueField row={draft.row} onChange={row => onChange(draft.key, row)} />)
    },
    {
      label: 'KPI',
      name: 'kpi',
      flex: true,
      minFlexWidth: 240,
      nonInteractive: true,
      renderer: (draft: TableItem) => kpiSelect(draft, 'kpi', OptionsKey.KPI)
    },
    {
      label: 'KPI Source',
      name: 'source',
      flex: true,
      minFlexWidth: 170,
      nonInteractive: true,
      renderer: (draft: TableItem) => kpiSelect(draft, 'source', OptionsKey.SOURCE)
    },
    {
      label: 'Campaign (Platform)',
      name: 'campaign_platform',
      flex: true,
      minFlexWidth: 300,
      nonInteractive: true,
      renderer: (draft: TableItem) => select(draft, 'campaign_platform', OptionsKey.CAMPAIGN, true)
    },
    {
      label: 'Ad Group (Platform)',
      name: 'ad_group_platform',
      flex: true,
      minFlexWidth: 300,
      nonInteractive: true,
      renderer: (draft: TableItem) => select(draft, 'ad_group_platform', OptionsKey.AD_GROUP, true)
    },
    {
      label: 'Issue',
      name: 'error',
      flex: true,
      minFlexWidth: 300,
      nonInteractive: true,
      renderer: (draft: TableItem) => (
        <div className='goal-intake-row-issue'>
          <span className='goal-intake-row-error' data-testid='goal-intake-row-error'>
            {errors.get(draft.key)}
          </span>
          {requiresKpi(draft.row) && (
            <span className='goal-intake-kpi-hint'>KPI not listed? ask Client Reporting</span>
          )}
        </div>
      )
    },
    {
      label: '',
      name: 'delete',
      width: 56,
      nonInteractive: true,
      renderer: (draft: TableItem) => (
        <IconButton
          appearance={Appearance.EMPTY}
          tooltip={{ text: 'Delete' }}
          data-testid='goal-intake-delete-row'
          onClick={() => onDelete(draft.key)}
        >
          <MdDeleteOutline />
        </IconButton>
      )
    }
  ];

  // Marker rows fill every column grey; the label sits in the first column.
  return columns.map((header, index) => ({
    ...header,
    renderer: (item: TableItem) =>
      item.markerLabel === undefined ? (
        <div className={cn('goal-intake-row', { striped: item.isStriped })}>
          {item.row.legacy && !LEGACY_EDITABLE_COLUMNS.has(header.name ?? '') ? (
            <div className='goal-intake-cell goal-intake-legacy-cell'>
              {getLegacyContent(header.name ?? '', item.row)}
            </div>
          ) : (
            header.renderer(item)
          )}
        </div>
      ) : (
        <div className={cn('goal-intake-marker', { 'first-marker': index === 0 })}>
          {index === 0 ? item.markerLabel : null}
        </div>
      )
  }));
};
